import { useEffect, useState } from 'react';
import { getStats } from '../api';
import type { StatsResponse } from '../types';

export default function Strategies() {
  const [data, setData] = useState<StatsResponse | null>(null);

  useEffect(() => {
    getStats().then(setData).catch(() => null);
    const id = setInterval(() => {
      getStats().then(setData).catch(() => null);
    }, 10000);
    return () => clearInterval(id);
  }, []);

  const maxPnl = data?.by_strategy.reduce((max, s) => Math.max(max, Math.abs(s.total_pnl)), 1) ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Strategies</h2>
        <span className="text-xs text-trader-muted">Auto-refresh: 10s</span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Total Trades</p>
          <p className="text-2xl font-bold">{data?.total_trades ?? '—'}</p>
        </div>
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Filled</p>
          <p className="text-2xl font-bold text-trader-profit">{data?.filled_trades ?? '—'}</p>
        </div>
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Strategies Active</p>
          <p className="text-2xl font-bold text-trader-info">{data?.by_strategy.length ?? '—'}</p>
        </div>
      </div>

      {/* Per-strategy breakdown */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-4">Per-Strategy Breakdown</h3>
        {!data?.by_strategy.length ? (
          <p className="text-trader-muted text-sm">No strategy data yet</p>
        ) : (
          <div className="space-y-3">
            {data.by_strategy.map((s) => {
              const pnlPct = Math.abs(s.total_pnl) / maxPnl * 100;
              const isProfit = s.total_pnl >= 0;
              return (
                <div key={s.strategy} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-mono">{s.strategy}</span>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-trader-muted">{s.count} trades</span>
                      <span className="text-trader-warn">Fees: ${s.total_fees.toFixed(2)}</span>
                      <span className={isProfit ? 'text-trader-profit' : 'text-trader-loss'}>
                        PnL: {isProfit ? '+' : ''}${s.total_pnl.toFixed(4)}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-trader-bg rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${isProfit ? 'bg-trader-profit/60' : 'bg-trader-loss/60'}`}
                      style={{ width: `${Math.max(pnlPct, 2)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* By side */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">By Side</h3>
        {!data?.by_side.length ? (
          <p className="text-trader-muted text-sm">No data</p>
        ) : (
          <div className="flex gap-6">
            {data.by_side.map((s) => (
              <div key={s.side} className="text-center">
                <p className={`text-2xl font-bold ${s.side === 'buy' ? 'text-trader-profit' : 'text-trader-loss'}`}>
                  {s.count}
                </p>
                <p className="text-xs text-trader-muted uppercase">{s.side}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
